// hooks/useGradeUpdate.ts
//
// Saves a course grade and brings BOTH consumers of it up to date.
//
// Two surfaces show a GPA and they come from different payloads:
//   - the semester card / header GPA read `academicPlan.gpa` (plan fetch)
//   - the degree audit's "Cumulative GPA" gate reads
//     `graduation_readiness.gpa`, computed server-side inside the audit payload
// The 8.62.1 bug was server-side: the grade write never evicted the cached
// audit, so the refetch kept receiving the stale payload. The audit refetch
// is dispatched here explicitly so this hook does not depend on the
// implementation detail that `usePlanCore.refreshPlan` happens to dispatch
// one too (belt and braces; the backend single-flight lock means a second
// request costs one round-trip, never a second audit computation).

import { useCallback } from 'react';
import { typedApi } from '../services/typedApiClient';
import { dispatchDegreeAuditRefresh } from '../utils/appEvents';

export type UpdateGradeFn = (
  semester: string,
  courseCode: string,
  grade: string | null,
) => Promise<void>;

export function useGradeUpdate(
  currentPlanId: number | null | undefined,
  refreshPlan: () => Promise<void>,
  showError: (message: string) => unknown,
): UpdateGradeFn {
  return useCallback(async (semester, courseCode, grade) => {
    if (currentPlanId == null) return;
    try {
      await typedApi.grades.update({
        plan_id: currentPlanId,
        semester,
        course_code: courseCode,
        // Cast: typedApi narrows to a letter-grade union; the planner UI
        // accepts arbitrary strings (validated server-side).
        grade: grade as Parameters<typeof typedApi.grades.update>[0]['grade'],
      });
      // Plan payload: semester-card + header GPA.
      await refreshPlan();
      // Audit payload: the Cumulative GPA graduation gate. The backend evicts
      // its audit cache on the grade write; this makes the UI refetch it.
      dispatchDegreeAuditRefresh(currentPlanId);
    } catch (error) {
      console.error('Failed to update grade:', error);
      showError('Failed to update grade');
    }
  }, [currentPlanId, refreshPlan, showError]);
}
